import { getControlTableData, getGraphsData } from "api/volSurfaceApi";
import { VolSurfaceContext } from "contexts/VolSurfaceContext";
import { useVolSurfaceControlTable } from "contexts/VolSurfaceControlTableContext";
import { getDataFromLocalStorage } from "helpers/generalHelper";
import { checkForApoShortcutName } from "helpers/volSurfaceHelper";
import useVolSurfaceWebSocket from "hooks/useVolSurfaceWebSocket";
import { useContext, useEffect, useRef } from "react";

function useControlTable({ start_date, end_date, setComparisonSurfaceId }) {
  const { setVolSurfaceData, setVolSurfaceBtnLoading, volSurfaceBtnLoading } =
    useContext(VolSurfaceContext);

  const {
    setVolSurfaceData: setRowsData,
    selectedFilters,
    setVolCalcTable,
    setRiskFree,
    setCostOfCarry,
    setAverage,
  } = useVolSurfaceControlTable();

  // Add ref to prevent multiple simultaneous calls
  const isRunningRef = useRef(false);
  const timeoutRef = useRef(null);
  const prevComparisonSurfaceIdRef = useRef(null);

  function getTableDate() {
    if (!selectedFilters?.product_id || isRunningRef.current) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the call
    timeoutRef.current = setTimeout(() => {
      isRunningRef.current = true;
      setVolSurfaceBtnLoading(true);

      // Check if only comparison_surface_id changed
      const isOnlyComparisonSurfaceChanged =
        prevComparisonSurfaceIdRef.current !== null &&
        prevComparisonSurfaceIdRef.current !==
          selectedFilters?.comparison_surface_id;

      // Check if risk_free changed
      // const isRiskFreeChanged =
      //   prevRiskFreeRef.current !== null &&
      //   prevRiskFreeRef.current !== selectedFilters?.risk_free;

      let queryParams = {
        product_contract_style_id: selectedFilters?.product_id,
        comparison_surface_id: selectedFilters?.comparison_surface_id,
      };

      if (selectedFilters?.company_id) {
        queryParams.company_id = selectedFilters?.company_id;
      }

      if (start_date && end_date) {
        queryParams.start_date = start_date;
        queryParams.end_date = end_date;
      }
      // Only include risk_free if it changed or if it's the first load
      const riskFreeChanged = getDataFromLocalStorage("IsRiskFreeChanged");
      if (riskFreeChanged) {
        queryParams.risk_free = selectedFilters?.risk_free;
      }

      // Only include average and cost_of_carry if not only comparison_surface_id changed
      if (
        checkForApoShortcutName(selectedFilters) &&
        !isOnlyComparisonSurfaceChanged
      ) {
        const averageAndCostOfCarryIsReloaded = getDataFromLocalStorage(
          "AverageAndCostOfCarryIsReloaded"
        );

        if (
          !averageAndCostOfCarryIsReloaded &&
          selectedFilters?.average !== null &&
          selectedFilters?.cost_of_carry !== null
        ) {
          queryParams.average = selectedFilters?.average;
          queryParams.cost_of_carry = selectedFilters?.cost_of_carry;
        }
      }

      // Update the previous values
      prevComparisonSurfaceIdRef.current =
        selectedFilters?.comparison_surface_id;

      getControlTableData(queryParams)
        .then((res) => {
          const data = res.data.data;
          setRowsData(data.main_control_table);
          setVolCalcTable(data.vol_calc_table);
          setComparisonSurfaceId(data?.comparison_surface_id);
          setRiskFree(
            data?.risk_free !== undefined && data?.risk_free !== null
              ? Number(data?.risk_free)
              : null
          );
          setAverage(
            data?.average !== undefined && data?.average !== null
              ? Number(data?.average)
              : null
          );
          setCostOfCarry(
            data?.cost_of_carry !== undefined && data?.cost_of_carry !== null
              ? Number(data?.cost_of_carry)
              : null
          );
        })
        .catch((err) => {
          setVolSurfaceData(null);
          setRowsData(null);
          setVolCalcTable(null);
          setComparisonSurfaceId(null);
          // setAverage(null);
          // setCostOfCarry(null);
          // setRiskFree(null);
          // delete selectedFilters.risk_free;
          // delete selectedFilters.average;
          // delete selectedFilters.cost_of_carry;
        })
        .finally(() => {
          setVolSurfaceBtnLoading(false);
          isRunningRef.current = false;
        });
    }, 50); // 50ms debounce
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  //Get Control Table Data
  useEffect(() => {
    if (!selectedFilters?.product_id || volSurfaceBtnLoading) return;
    getTableDate();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFilters?.comparison_surface_id,
    selectedFilters?.product_id,
    selectedFilters?.risk_free,
    selectedFilters?.average,
    selectedFilters?.cost_of_carry,
    selectedFilters?.company_id,
    start_date,
    end_date,
  ]);

  //websocket call for control table
  useVolSurfaceWebSocket(selectedFilters, () => {
    if (!selectedFilters?.product_id) return;
    getTableDate();
    getGraphsData({
      product_contract_style_id: selectedFilters?.product_id,
      comparison_surface_id: selectedFilters?.comparison_surface_id,
      start_date: start_date,
      end_date: end_date,
    }).then((res) => {
      const response = res.data.data;

      setVolSurfaceData(response);
    });
  });
}

export default useControlTable;
